import starIcon from '@backrs_ui/common-ui/src/assets/images/star.png';
import verifiedTickIcon from '@backrs_ui/common-ui/src/assets/images/verified-tick.png';

import { Box, SxProps, Theme, Typography } from '@mui/material';

export type ReviewCardProps = Readonly<{
  quote: string;
  profileImage: string;
  name: string;
  subtitle: string;
  shrinkText: boolean;
}>;

const STAR_COUNT = 5;

export const ReviewCard = ({
  quote,
  profileImage,
  name,
  subtitle,
  shrinkText,
}: ReviewCardProps) => {
  return (
    <Box sx={cardContainerSx(shrinkText)}>
      <Box sx={contentContainerSx}>
        <Box sx={starsContainerSx}>
          {Array.from({ length: STAR_COUNT }).map((_, index) => (
            <Box key={index} component="img" src={starIcon} alt="" sx={starIconSx} />
          ))}
        </Box>

        <Typography variant={shrinkText ? 'text-sm-medium' : 'text-md-medium'} sx={quoteSx}>
          {quote}
        </Typography>
      </Box>

      <Box sx={profileContainerSx}>
        <Box component="img" src={profileImage} alt={name} sx={avatarSx} />
        <Box sx={profileTextContainerSx}>
          <Box sx={nameRowSx}>
            <Typography
              variant="text-md-semibold"
              color={({ backrs }) => backrs.colors['Gray/900']}
            >
              {name}
            </Typography>
            <Box component="img" src={verifiedTickIcon} alt="verified" sx={verifiedTickSx} />
          </Box>
          <Typography variant="text-md-regular" color={({ backrs }) => backrs.colors['Gray/600']}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const cardContainerSx =
  (shrinkText: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: shrinkText ? backrs.spacing['spacing-xl'] : backrs.spacing['spacing-4xl'],
    padding: shrinkText ? backrs.spacing['spacing-3xl'] : backrs.spacing['spacing-4xl'],
    backgroundColor: backrs.colors['Gray/50'],
    borderRadius: backrs.radius['radius-xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    width: '100%',
    height: '100%',
  });

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  alignItems: 'flex-start',
});

const starsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-xs'],
  alignItems: 'center',
});

const quoteSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
  whiteSpace: 'pre-line',
});

const profileContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  alignItems: 'flex-start',
  width: '100%',
  marginTop: 'auto',
});

const avatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: 48,
  height: 48,
  borderRadius: '200px',
  objectFit: 'cover',
  border: `0.75px solid ${backrs.colors['Colors/Base/Black']}`,
});

const profileTextContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
};

const nameRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-xs'],
  alignItems: 'center',
});

const starIconSx: SxProps<Theme> = { width: 20, height: 20 };

const verifiedTickSx: SxProps<Theme> = { width: 16, height: 16 };
